import { Image as ImagePrimitive, Source } from '@unpic/react/base';
import { generateImageSrc } from '@/lib/shared';
import { cn } from '@worldcoo/ui/lib/utils';
import { useRouteContext } from '@tanstack/react-router';
import { getImgproxyUrl } from '@/lib/cdn';

/** Unpic's default resolutions extended with smaller sizes for mobile 1x displays. */
const RESOLUTIONS = [160, 320, 480, 640, 750, 828, 960, 1080, 1280, 1668, 1920];

function getBreakpoints(width: number): number[] {
	const doubleWidth = width * 2;
	return [width, doubleWidth, ...RESOLUTIONS.filter((w) => w < doubleWidth)];
}

/** Generate a stepped `sizes` from breakpoints so the browser doesn't assume 100vw. */
function getSizes(width: number, breakpoints: number[]): string {
	// There is not a wide support for toSorted() in the browser
	// oxlint-disable-next-line unicorn/no-array-sort
	const steps = [...new Set(breakpoints)].filter((bp) => bp <= width).sort((a, b) => b - a);
	return steps.map((bp, i) => (i === steps.length - 1 ? `${bp}px` : `(min-width: ${bp}px) ${bp}px`)).join(', ');
}

export function Image(props: {
	src: string | undefined;
	height: number;
	aspectRatio: number;
	objectFit?: 'contain' | 'cover';
	alt?: string;
	/** When true, sets fetchPriority="high" and loading="eager". Default is lazy. */
	priority?: boolean;
	className?: string;
}) {
	const { baseUrl } = useRouteContext({ from: '__root__' });

	if (!props.src) {
		return <div className={cn(props.className, 'bg-transparent')} style={{ aspectRatio: props.aspectRatio }} />;
	}

	let src = props.src;
	if (props.src.startsWith('/')) {
		try {
			src = new URL(props.src, baseUrl).href;
		} catch {
			// baseUrl is somehow invalid – fall back to the relative path and let the browser resolve it
		}
	}

	const resizeType = (props.objectFit ?? 'cover') === 'contain' ? 'fit' : 'fill';
	const transformer = (src: string | URL, opts: { height?: number; width?: number; format?: 'webp' | 'avif' }) =>
		generateImageSrc(src.toString(), { ...opts, quality: opts.format === 'avif' ? 60 : 70, resizeType });

	const hasImgproxy = !!getImgproxyUrl();
	const width = Math.round(props.height * props.aspectRatio);
	const breakpoints = getBreakpoints(width);
	const sizes = getSizes(width, breakpoints);

	return (
		<picture>
			{hasImgproxy && (
				<Source
					src={src}
					layout="constrained"
					width={width}
					height={props.height}
					type="image/avif"
					priority={props.priority}
					sizes={sizes}
					breakpoints={breakpoints}
					transformer={(src: string | URL, opts: { height?: number; width?: number }) =>
						transformer(src, { ...opts, format: 'avif' })
					}
				/>
			)}
			<ImagePrimitive
				src={src}
				layout="constrained"
				width={width}
				height={props.height}
				objectFit={props.objectFit ?? 'cover'}
				className={props.className}
				alt={props.alt}
				priority={props.priority}
				sizes={sizes}
				breakpoints={breakpoints}
				transformer={transformer}
			/>
		</picture>
	);
}
